import React, { useRef } from 'react'
import { addLog } from '../../components/LoggerUI'

interface HiddenOTPInputProps {
  numberOfDigits: number
  otp: string[]
  activeIndex: number
  onOTPChange: (otp: string[], isOTPAutoFilled?: boolean) => void
  onActiveIndexChange: (index: number) => void
  onAutofill: (digits: string[]) => void
  isDisabled: boolean
}

export const HiddenOTPInput: React.FC<HiddenOTPInputProps> = ({
  numberOfDigits,
  otp,
  activeIndex,
  onOTPChange,
  onActiveIndexChange,
  onAutofill,
  isDisabled
}) => {
  const hiddenInputRef = useRef<HTMLInputElement>(null)

  // Hidden input change handler
  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value

    if (isDisabled) return

    // Handle SMS autofill case (Chrome on iOS)
    if (newValue.length > 1 && new RegExp(`^\\d{${numberOfDigits}}$`).test(newValue)) {
      // Create new OTP array with distributed digits
      const otpArray = newValue.split('')
      onAutofill(otpArray)
      // Clear the hidden input after autofill
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = ''
      }
      return
    }

    // Handle normal single digit input
    if (/^\d{0,1}$/.test(newValue)) {
      const newOtp = [...otp]
      newOtp[activeIndex] = newValue
      onOTPChange(newOtp)

      if (newValue) {
        addLog(`OTP digit ${activeIndex + 1} entered: ${newValue}`, 'info')
      }

      // Clear the hidden input after processing
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = ''
      }
    }
  }

  // Hidden input key down handler
  const handleHiddenInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otp[activeIndex] === '') {
        // If current input is empty, move to previous input
        if (activeIndex > 0) {
          const newOtp = [...otp]
          newOtp[activeIndex - 1] = ''
          onOTPChange(newOtp)
          onActiveIndexChange(activeIndex - 1)
          addLog(`Backspace: cleared input ${activeIndex}`, 'info')
        }
      } else {
        // If current input has value, just clear it
        const newOtp = [...otp]
        newOtp[activeIndex] = ''
        onOTPChange(newOtp)
        addLog(`Backspace: cleared input ${activeIndex + 1}`, 'info')
      }
    }
  }

  // Hidden input paste handler
  const handleHiddenInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    if (new RegExp(`^\\d{${numberOfDigits}}$`).test(pastedData)) {
      const otpDigits = pastedData.split('')
      onAutofill(otpDigits)
    }
  }

  return (
    <input
      id='hidden-otp-input'
      ref={hiddenInputRef}
      type='number'
      inputMode='numeric'
      pattern='[0-9]*'
      autoComplete='one-time-code'
      onChange={handleHiddenInputChange}
      onKeyDown={handleHiddenInputKeyDown}
      onPaste={handleHiddenInputPaste}
      disabled={isDisabled}
      className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
      style={{
        position: 'absolute',
        top: 0,
        left: 2,
        right: 0,
        bottom: 0,
        zIndex: 1,
        fontSize: '1px',
        color: 'transparent',
        background: 'transparent',
        caretColor: '#FFFFFF',
        border: 'none',
        outline: 'none'
      }}
    />
  )
}
